import React from "react";
import { useUser } from "@hooks/useUser";
import { ActorType, AuditLog } from "@common/types/auditLog";
import { UserType } from "@common/types/user";
import {
    KICK_ACTOR_LABEL,
    KICK_AGENT_ACTOR_LABEL,
    KICK_SUPPORT_LABEL,
    PLATFORM_API_ACTOR_LABEL,
} from "@common/constants/actor";
import { ActorName } from "@components/history/components/ActorName";
import { useVisibleHistoryChannel } from "@components/history/hooks/useVisibleHistoryChannel";
import {
    resolveActorChannelLabel,
    resolveActorDisplayName,
} from "@components/history/utils/actorUtils";
import { useIsVisibleAgentActor } from "./hooks/useIsVisibleAgentActor";
import styles from "./AuditLogItem.module.scss";

interface AuditLogActorProps {
    actorId: number | null;
    actorName: string | null;
    actorType: ActorType;
    log?: Pick<
        AuditLog,
        "sourceType" | "diff" | "changeType" | "metadata" | "channel"
    >;
}

function extractSystem(
    metadata: AuditLog["metadata"] | undefined,
): string | null {
    if (!metadata) {
        return null;
    }
    const context = metadata.context;
    if (typeof context !== "object" || context === null) {
        return null;
    }
    const system = (context as Record<string, unknown>).system;
    return typeof system === "string" ? system : null;
}

const toLogActorParams = ({
    actorType,
    log,
    isAgentActor,
}: {
    actorType: ActorType;
    log: NonNullable<AuditLogActorProps["log"]>;
    isAgentActor: boolean;
}) => ({
    actorType,
    sourceType: log.sourceType,
    fields: log.diff.map((diff) => diff.field),
    changeTypes: [log.changeType],
    system: extractSystem(log.metadata),
    isAgentActor,
});

export const AuditLogActor: React.FC<AuditLogActorProps> = ({
    actorId,
    actorName,
    actorType,
    log,
}) => {
    const currentUser = useUser();
    const isAgentActor = useIsVisibleAgentActor(actorType);
    const visibleChannel = useVisibleHistoryChannel(log?.channel);

    const displayName = React.useMemo(() => {
        if (
            actorType === ActorType.SUPPORT &&
            currentUser.type === UserType.SUPPORT
        ) {
            return actorName ?? KICK_SUPPORT_LABEL;
        }

        if (isAgentActor) {
            return KICK_AGENT_ACTOR_LABEL;
        }

        if (!log) {
            if (actorType === ActorType.SYSTEM) {
                return KICK_ACTOR_LABEL;
            }
            if (actorType === ActorType.SUPPORT) {
                return KICK_SUPPORT_LABEL;
            }
            if (actorType === ActorType.ORGANIZATION) {
                return PLATFORM_API_ACTOR_LABEL;
            }
            if (actorId && currentUser.id === actorId) {
                return "You";
            }
            return actorName ?? `ID: ${actorId ?? "Unknown"}`;
        }

        return resolveActorDisplayName({
            ...toLogActorParams({ actorType, log, isAgentActor }),
            actorName,
            actorId,
            currentUserId: currentUser.id,
        });
    }, [
        actorId,
        actorName,
        actorType,
        isAgentActor,
        currentUser.id,
        currentUser.type,
        log,
    ]);

    const channelLabel = log
        ? resolveActorChannelLabel({
              ...toLogActorParams({ actorType, log, isAgentActor }),
              channel: visibleChannel,
          })
        : undefined;

    return (
        <span className={styles.actorName}>
            <ActorName
                name={displayName}
                channelLabel={channelLabel}
                className={styles.actorNameText}
            />
        </span>
    );
};
